import { useEffect, useState } from 'react'
import ListCloseIcon from '@pc/components/Works/Panel/shared/List/ListCloseIcon'
import ListItem from '@pc/components/Works/Panel/shared/List/ListItem'
import ListItemDescription from '@pc/components/Works/Panel/shared/List/ListItemDescription'
import ListItemName from '@pc/components/Works/Panel/shared/List/ListItemName'
import ListItemParagraph from '@pc/components/Works/Panel/shared/List/ListItemParagraph'
import ListOpenIcon from '@pc/components/Works/Panel/shared/List/ListOpenIcon'
import type { RawLineCompany, Line, Station } from '@pc/components/Works/Panel/types'
import LineList from './LineList'
import type { ComponentPropsWithoutRef, MouseEvent } from 'react'

interface PropsType {
  lineCompany: RawLineCompany
  lines: Line[]
  mappedStations: Record<string, Station[]>
  selectedStationIds: string[]
  changeStationIds: (stationIds: string[]) => void
}

const LineCompanyListItem = (props: ComponentPropsWithoutRef<'li'>) => <ListItem className="cursor-pointer" {...props} />

const LineCompanyListItemParagraph = ({ enable, ...props }: { enable: boolean } & ComponentPropsWithoutRef<'dl'>) => (
  <ListItemParagraph
    className={`relative w-full [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:right-4 [&_svg]:-translate-y-1/2 ${enable ? 'border-b border-b-gray-500 bg-[#f5f5f5]' : ''}`}
    {...props}
  />
)

const LineCompanyListItemName = (props: ComponentPropsWithoutRef<'dt'>) => <ListItemName className="min-w-42 font-bold" {...props} />

const LineCompanyListDescription = (props: ComponentPropsWithoutRef<'dd'>) => <ListItemDescription className="mr-8" {...props} />

const LineCompanyItem = (props: PropsType) => {
  const { lineCompany, lines, mappedStations, selectedStationIds, changeStationIds } = props
  const [childrenIsOpen, setChildrenIsOpen] = useState(false)

  const toggleChildren = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setChildrenIsOpen(!childrenIsOpen)
  }

  const joinLineNames = (lines: Line[]) => {
    return lines.map((line) => line.attributes.name).join('、')
  }

  useEffect(() => {
    const stationIdsInLineCompanies = lines
      .map((line) => {
        const stations = mappedStations[line.id]
        return stations.map((station) => station.attributes.compat_id.toString())
      })
      .flat()
    const isSelectedBeforehand = stationIdsInLineCompanies.some((stationId) => selectedStationIds.includes(stationId))
    if (isSelectedBeforehand) {
      setChildrenIsOpen(true)
    }
  }, [lines, mappedStations, selectedStationIds])

  return (
    <LineCompanyListItem>
      <LineCompanyListItemParagraph onClick={toggleChildren} enable={childrenIsOpen}>
        <LineCompanyListItemName>{lineCompany.attributes.name}</LineCompanyListItemName>
        <LineCompanyListDescription>{joinLineNames(lines)}</LineCompanyListDescription>
        {childrenIsOpen ? <ListCloseIcon /> : <ListOpenIcon />}
      </LineCompanyListItemParagraph>
      <ul className="w-full divide-y divide-gray-500">
        {childrenIsOpen ? (
          <LineList lines={lines} mappedStations={mappedStations} selectedStationIds={selectedStationIds} changeStationIds={changeStationIds} />
        ) : null}
      </ul>
    </LineCompanyListItem>
  )
}

export default LineCompanyItem
